import { Dot } from "lucide-react";
import React, { useContext, useEffect, useRef, useState } from "react";
import Edit from "../../../../assets/images/icons/editIconNew.svg";
import DocumentViewer from "./DocumentViewer";
import MainServices from "../../../../services/MainServices";
import { notifyWarn } from "../../../../assets/elements/Toast";
import { toast } from "react-toastify";
import { AuthContext } from "../../../../context/AuthContext";
import { Field, FieldArray, Form, Formik } from "formik";
import * as Yup from "yup";
import CustomErrorMessage from "../../../../assets/common/CustomeErrorMessage";
import ButtonLoading from "../../../../assets/loaders/ButtonLoading";
import PDF from "./svg/Pdf";
import AsyncSelect from "react-select/async";
import dateIcon from "../../../../assets/images/dateIcon.svg";
import useRTLContext from "../../../../hooks/useRTLContext";
import { useTranslation } from "react-i18next";
import EditingRestrictionNotice from "./EditRestrictionNotice";
import { documentValidation } from "../employeesModals/validation";

const VisaNew = ({
  visaDetails,
  personalInfo,
  handleGetTableData,
  empPublicId,
  getEmployeeRegistrationData,
  currentEmployeeEditing = false,
}) => {
  const { isRTL, direction } = useRTLContext();
  const { t, i18n } = useTranslation();
  const [documentViewerOpen, setDocumentViewerOpen] = useState(false);
  const [selectedDocument, setSelectedDocument] = useState(null);
  const [visaType, setVisaType] = useState([]);
  const fileInputRef1 = useRef(null);
  const fileInputRef2 = useRef(null);
  const [fileNames, setFileNames] = useState({
    frontDocument: visaDetails?.frontDocument || "",
    backDocument: visaDetails?.backDocument || "",
  });
  const [visaError, setVisaError] = useState(null);
  const [initialValues, setInitialValues] = useState({});
  const [editingSections, setEditingSections] = useState({
    visa: false,
    documents: false,
  });
  const [loading, setLoading] = useState(false);
  const { fileUpload, updateEmployeeRegistrationMaster, updateVisaInfo } =
    MainServices();

  useEffect(() => {
    getVisaTypeOptions();
  }, []);

  const loadVisaTypeOptions = (inputValue, callback) => {
    const filteredOptions = visaType.filter((option) =>
      option.label.toLowerCase().includes(inputValue.toLowerCase())
    );
    callback(inputValue ? filteredOptions : visaType);
  };

  // Fetching visaType options
  const getVisaTypeOptions = async () => {
    setVisaType([
      { value: "business", label: "Business Visa" },
      { value: "iqama visa", label: "Iqama Visa" },
      { value: "student", label: "Student Visa" },
      { value: "work", label: "Work Visa" },
      { value: "transit", label: "Transit Visa" },
      { value: "diplomatic", label: "Diplomatic Visa" },
      { value: "medical", label: "Medical Visa" },
    ]);
  };

  const handleDocumentChange = (e, setFieldValue) => {
    const { name, files } = e.target;

    if (files && files[0]) {
      const fileData = {
        file: files[0],
        fileName: files[0].name,
      };

      setFileNames((prev) => ({
        ...prev,
        [name]: fileData,
      }));

      const formikField =
        name === "frontDocument" ? "visaDocFront" : "visaDocBack";
      setFieldValue(formikField, fileData);

      console.log("File uploaded:", files[0].name);
    } else {
      console.log("No file selected");
    }
  };

  const handleRemoveDocument = (documentType, setFieldValue) => {
    setFileNames((prev) => ({
      ...prev,
      [documentType]: "",
    }));

    if (documentType === "frontDocument") {
      fileInputRef1.current.value = "";
      setFieldValue("visaDocFront", "");
    } else if (documentType === "backDocument") {
      fileInputRef2.current.value = "";
      setFieldValue("visaDocBack", "");
    }
  };

  const handleViewDocument = (documentUrl, documentType, documentLabel) => {
    setSelectedDocument({
      url: documentUrl,
      type: getFileType(documentUrl),
      label: documentLabel,
    });
    setDocumentViewerOpen(true);
  };

  const getFileType = (url) => {
    try {
      const urlObj = new URL(url); // Ensure it's a valid URL
      const pathname = urlObj.pathname;
      const extension = pathname.split(".").pop().toLowerCase();

      return extension || "unknown"; // Return "unknown" if no extension found
    } catch (error) {
      return "invalid URL"; // Handle invalid URLs
    }
  };

  const handleSubmit = async (values) => {
    setLoading(true);
    const updatedVisaInfo = {
      ...(values?.visaNo && { visaNo: values.visaNo }),
      ...(values?.visaExpiry && { expiryDate: values.visaExpiry }),
      ...(values?.visaIssue && { issueDate: values.visaIssue }),
      ...(values?.visaIssuePlace && {
        placeOfIssue: values.visaIssuePlace,
      }),
      ...(values?.visaType?.value && {
        type: values.visaType.value,
      }),
    };

    let formattedPersonalInfo = {
      ...(personalInfo?.firstName && { firstName: personalInfo.firstName }),
      ...(personalInfo?.secondName && { secondName: personalInfo.secondName }),
      ...(personalInfo?.thirdName && { thirdName: personalInfo.thirdName }),
      ...(personalInfo?.lastName && { lastName: personalInfo.lastName }),
      ...(personalInfo?.phone && { phone: personalInfo.phone }),
      ...(personalInfo?.email && { email: personalInfo.email }),
      ...(personalInfo?.dobHijri && { dobHijri: personalInfo.dobHijri }),
      ...(personalInfo?.dobGregorian && {
        dobGregorian: personalInfo.dobGregorian,
      }),
      ...(personalInfo?.religion && {
        religion: personalInfo.religion,
      }),
      ...(personalInfo?.maritalStatus && {
        maritalStatus: personalInfo.maritalStatus,
      }),
      ...(personalInfo?.gender && { gender: personalInfo.gender }),
      ...(personalInfo?.citizen && { citizen: personalInfo.citizen }),

      ...(personalInfo?.citizen === "false" &&
        personalInfo?.nationality && {
          nationality: personalInfo?.nationality,
        }),
      ...(personalInfo?.citizen === "true" &&
        personalInfo?.nationalId && {
          nationalId: personalInfo.nationalId,
        }),
    };

    const details = {
      ...(formattedPersonalInfo && formattedPersonalInfo),
      documents: {
        visaInfo: updatedVisaInfo,
      },
    };
    try {
      const response = await updateEmployeeRegistrationMaster(
        details,
        personalInfo?.publicId,
        personalInfo?.concurrencyStamp
      );

      if (response?.data) {
        handleEditToggle("visa");
        setVisaError(null);
        toast("Visa Details Updated Successfully!", {
          style: { zIndex: 9999 },
        });
        getEmployeeRegistrationData(empPublicId);
        handleGetTableData();
      } else {
        toast.error("Failed to update passport", {
          style: { zIndex: 9999 },
        });
      }
    } catch (error) {
      toast.error(
        error?.response?.data?.message || "An unexpected error occurred",
        {
          style: { zIndex: 9999 },
        }
      );
      console.error("Error", error);
    } finally {
      setLoading(false);
    }
  };
  const handleUpdateDocuments = async () => {
    if (!visaDetails?.visaNo) {
      setVisaError(
        "Please complete all required Visa details before submitting documents"
      );
      return;
    } else {
      setVisaError(null); // Clear error if details are complete
    }
    const files = [];

    // Add files only if they exist
    if (fileNames?.frontDocument?.file) {
      files.push(fileNames.frontDocument.file);
    }
    if (fileNames?.backDocument?.file) {
      files.push(fileNames.backDocument.file);
    }

    try {
      let updatedFileNames = {};

      // If there are files to upload, call the fileUpload API
      if (files.length > 0) {
        const response = await fileUpload(files);
        if (response?.data) {
          const responseData = response?.data?.data;
          let currentIndex = 0;

          // Handle front document
          if (fileNames?.frontDocument?.file) {
            updatedFileNames.frontDocument =
              responseData[currentIndex]?.fileUrl;
            currentIndex++;
          } else {
            updatedFileNames.frontDocument = fileNames.frontDocument || ""; // Use existing value or empty
          }

          // Handle back document
          if (fileNames?.backDocument?.file) {
            updatedFileNames.backDocument = responseData[currentIndex]?.fileUrl;
          } else {
            updatedFileNames.backDocument = fileNames.backDocument || ""; // Use existing value or empty
          }
        } else {
          throw new Error(response?.message || "Unknown error");
        }
      } else {
        // If no files to upload, directly use the existing fileNames values
        updatedFileNames = {
          frontDocument: fileNames.frontDocument, // Use existing value or empty
          backDocument: fileNames.backDocument, // Use existing value or empty
        };
      }

      // Prepare the personal info object
      let formattedPersonalInfo = {
        ...(personalInfo?.firstName && { firstName: personalInfo.firstName }),
        ...(personalInfo?.secondName && {
          secondName: personalInfo.secondName,
        }),
        ...(personalInfo?.thirdName && { thirdName: personalInfo.thirdName }),
        ...(personalInfo?.lastName && { lastName: personalInfo.lastName }),
        ...(personalInfo?.phone && { phone: personalInfo.phone }),
        ...(personalInfo?.email && { email: personalInfo.email }),
        ...(personalInfo?.dobHijri && { dobHijri: personalInfo.dobHijri }),
        ...(personalInfo?.dobGregorian && {
          dobGregorian: personalInfo.dobGregorian,
        }),
        ...(personalInfo?.religion && { religion: personalInfo.religion }),
        ...(personalInfo?.maritalStatus && {
          maritalStatus: personalInfo.maritalStatus,
        }),
        ...(personalInfo?.gender && { gender: personalInfo.gender }),
        ...(personalInfo?.citizen && { citizen: personalInfo.citizen }),

        ...(personalInfo?.citizen === "false" &&
          personalInfo?.nationality && {
            nationality: personalInfo?.nationality,
          }),
        ...(personalInfo?.citizen === "true" &&
          personalInfo?.nationalId && {
            nationalId: personalInfo.nationalId,
          }),
      };

      // Prepare the updated national ID info
      const updatedVisaInfo = {
        frontDocument: updatedFileNames.frontDocument,
        backDocument: updatedFileNames.backDocument,
      };

      // Prepare the final payload
      const details = {
        ...(formattedPersonalInfo && formattedPersonalInfo),
        documents: {
          visaInfo: updatedVisaInfo,
        },
      };

      // Call the update API
      const updateResponse = await updateEmployeeRegistrationMaster(
        details,
        personalInfo?.publicId,
        personalInfo?.concurrencyStamp
      );

      if (updateResponse?.data) {
        toast("Visa Documents Updated Successfully!", {
          style: { zIndex: 999999999999999 },
        });
        getEmployeeRegistrationData(empPublicId);
        handleGetTableData();
        // Toggle back to "Edit" mode
        handleEditToggle("documents");
      }
    } catch (error) {
      console.error("Error updating national ID information:", error);
      notifyWarn(
        error?.response?.data?.message || "An unexpected error occurred"
      );
    } finally {
      setLoading(false);
    }
  };

  const getValidationSchema = (section) => {
    const baseSchema = {
      visaNo: Yup.string().required("Visa Number is required"),
      visaExpiry: Yup.date()
        .required("Visa Expiry is required")
        .typeError("Invalid date format"),
      visaIssue: Yup.date()
        .required("Visa Issue Date is required")
        .typeError("Invalid date format"),
      visaIssuePlace: Yup.string().required("Visa Issue Place is required"),
    };

    const documentSchema = {
      visaDocFront: documentValidation(),
      visaDocBack: documentValidation(),
    };

    if (section === "visa") {
      return Yup.object().shape(baseSchema);
    } else if (section === "documents") {
      return Yup.object().shape(documentSchema);
    } else {
      return Yup.object().shape({});
    }
  };

  const formatDateToYYYYMMDD = (date) => {
    if (!date) return "";

    const isISODate = /^\d{4}-\d{2}-\d{2}T/.test(date);
    const isDDMMYYYY = /^\d{2}\/\d{2}\/\d{4}$/.test(date);
    const isYYYYMMDD = /^\d{4}-\d{2}-\d{2}$/.test(date);

    let formattedDate = "";

    if (isISODate) {
      formattedDate = new Date(date).toISOString().split("T")[0];
    } else if (isDDMMYYYY) {
      const [day, month, year] = date.split("/");
      formattedDate = `${year}-${month.padStart(2, "0")}-${day.padStart(
        2,
        "0"
      )}`;
    } else if (isYYYYMMDD) {
      formattedDate = date;
    }

    return formattedDate;
  };

  function handleOpenFileInput() {
    fileInputRef1.current.click();
  }

  function handleOpenFileInput2() {
    fileInputRef2.current.click();
  }

  const formatDate = (dateString) => {
    if (!dateString) return ""; // Handle empty values
    const date = new Date(dateString);
    const yyyy = date.getFullYear();
    const mm = String(date.getMonth() + 1).padStart(2, "0");
    const dd = String(date.getDate()).padStart(2, "0");
    return `${yyyy}-${mm}-${dd}`;
  };

  const formatDateToDotFormat = (dateString) => {
    if (!dateString) return ""; // Handle empty or invalid input

    const date = new Date(dateString);

    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0"); // Months are 0-based
    const year = date.getFullYear();

    return `${day}.${month}.${year}`;
  };

  const formatDateForInput = (dateString) => {
    if (!dateString) return ""; // Handle empty or undefined values

    const date = new Date(dateString);

    // Convert to YYYY-MM-DD format
    return date.toISOString().split("T")[0];
  };

  const calculateExpiryDays = (dateString) => {
    if (!dateString) return null;
    const expiryDate = new Date(dateString);
    if (isNaN(expiryDate)) return null;
    const diffInMs = expiryDate - new Date();
    const diffInDays = Math.ceil(diffInMs / (1000 * 60 * 60 * 24));
    return diffInDays > 0 ? `Expiry In ${diffInDays} Days` : "Expired";
  };

  const handleEditToggle = (section, values) => {
    setEditingSections((prev) => {
      const isCurrentlyOpen = prev[section];
      const newState = {
        visa: false,
        documents: false,
      };

      // If the clicked section was previously closed, open it
      if (!isCurrentlyOpen) {
        newState[section] = true;
      }

      return newState;
    });

    if (values) {
      setInitialValues(values);
    }
  };

  const asyncSelectStyles = {
    control: (provided) => ({
      ...provided,
      width: "100%",
      height: "56px",
      padding: "0 6px",
      borderRadius: "8px",
      display: "flex",
      justifyContent: "space-between",
      background: "#f9fafb",
      border: "1px solid #eaecf0",
      fontFamily: "Poppins, sans-serif",
      fontSize: "12px",
      fontWeight: 400,
      lineHeight: "18px",
      textAlign: "left",
      color: "#111827",
    }),
    placeholder: (provided) => ({
      ...provided,
      color: "#A0A0A0",
    }),
    singleValue: (provided) => ({
      ...provided,
      color: "#04080b",
    }),
    menu: (provided) => ({
      ...provided,
      borderRadius: "8px",
      boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)",
    }),
    option: (provided, state) => ({
      ...provided,
      background: state.isFocused ? "#f3f4f6" : "#fff",
      color: "#04080b",
      padding: "10px 16px",
      cursor: "pointer",
      fontSize: "12px",
      lineHeight: "18px",
    }),
    menuList: (provided) => ({
      ...provided,
      maxHeight: "200px",
      overflowY: "auto",
    }),
  };

  console.log("visaDetails: ", visaDetails);

  return (
    <>
      <div className="tab-section">
        {/* Iqama Section */}
        <Formik
          enableReinitialize
          initialValues={{
            visaNo: visaDetails?.visaNo || "",
            visaExpiry: visaDetails?.expiryDate || null,
            vNationality: visaDetails?.nationalityInfo
              ? {
                  label: visaDetails.nationalityInfo.name,
                  value: visaDetails.nationalityInfo.publicId,
                }
              : visaDetails?.nationality || null,
            visaIssue: visaDetails?.issueDate || null,
            visaIssuePlace: visaDetails?.placeOfIssue || "",
            visaType: {
              label: visaDetails?.type || "",
              value: visaDetails?.type || "",
            },
            visaDocFront: visaDetails?.frontDocument || "",
            visaDocBack: visaDetails?.backDocument || "",
          }}
          validationSchema={
            editingSections.visa
              ? getValidationSchema("visa")
              : editingSections.documents
              ? getValidationSchema("documents")
              : undefined
          }
          onSubmit={(values) => {
            if (editingSections.visa) {
              handleSubmit(values);
            } else if (editingSections.documents) {
              handleUpdateDocuments(values);
            }
          }}
        >
          {({ values, errors, touched, setFieldValue, resetForm }) => (
            <>
              <div className="tab-header-container">
                <h3 className="tab-heading">{t("Visa")}</h3>
                {currentEmployeeEditing ? (
                  <EditingRestrictionNotice message="Read-only" />
                ) : (
                  !editingSections.visa && (
                    <div
                      className="tab-header-edit-container"
                      onClick={() => handleEditToggle("visa", values)}
                    >
                      <img src={Edit} alt="edit icon" />
                      <span className="edit-txt">{t("Edit")}</span>
                    </div>
                  )
                )}
              </div>
              <Form className="form-section">
                <div className="add-body-content-container-pr">
                  <div className="add-body-content-header">
                    <h3 className="add-body-content-heading">{t("Visa")}</h3>
                  </div>
                  <div className="add-body-content-grid">
                    {/* Visa Number */}
                    <div className="form-group">
                      <div className="add-body-input-section">
                        <div className="add-label-container">
                          <label className="add-label" htmlFor="visaNo">
                            {t("Visa Number")}
                          </label>
                          {editingSections.visa && (
                            <span className="add-req-mark">*</span>
                          )}
                        </div>
                        <div className="add-input-wrapper">
                          {editingSections.visa ? (
                            <input
                              id="visaNo"
                              name="visaNo"
                              className="add-input"
                              placeholder={t("Enter Visa Number")}
                              value={values?.visaNo}
                              onChange={(e) =>
                                setFieldValue("visaNo", e.target.value)
                              }
                            />
                          ) : (
                            <span>{values?.visaNo || t("N/A")}</span>
                          )}
                        </div>
                      </div>
                      <CustomErrorMessage
                        name="visaNo"
                        errors={errors}
                        touched={touched}
                      />
                    </div>

                    {/* Visa Expiry */}
                    <div className="form-group">
                      <div className="add-body-input-section">
                        <div className="add-label-container">
                          <label className="add-label" htmlFor="visaExpiry">
                            {t("Visa Expiry")}
                          </label>
                          {editingSections.visa && (
                            <span className="add-req-mark">*</span>
                          )}
                        </div>
                        <div className="add-input-wrapper">
                          {editingSections.visa ? (
                            <div className="add-input-wrapper">
                              <input
                                type="date"
                                id="visaExpiry"
                                name="visaExpiry"
                                className={`add-wrap-input ${
                                  !values?.visaExpiry ||
                                  values.visaExpiry === "dd-mm-yyyy"
                                    ? "empty"
                                    : ""
                                }`}
                                placeholder="DD/MM/YYYY"
                                value={
                                  values?.visaExpiry
                                    ? formatDateToYYYYMMDD(values.visaExpiry)
                                    : ""
                                }
                                onChange={(e) =>
                                  setFieldValue("visaExpiry", e.target.value)
                                }
                              />
                              <img
                                src={dateIcon}
                                alt="date icon"
                                className="add-date-input-in"
                              />
                            </div>
                          ) : (
                            <span>
                              {formatDateToYYYYMMDD(values?.visaExpiry) ||
                                t("N/A")}
                            </span>
                          )}
                        </div>
                      </div>
                      <CustomErrorMessage
                        name="visaExpiry"
                        errors={errors}
                        touched={touched}
                      />
                    </div>

                    {/* Visa Issue */}
                    <div className="form-group">
                      <div className="add-body-input-section">
                        <div className="add-label-container">
                          <label className="add-label" htmlFor="visaIssue">
                            {t("Visa Issue")}
                          </label>
                          {editingSections.visa && (
                            <span className="add-req-mark">*</span>
                          )}
                        </div>
                        <div className="add-input-wrapper">
                          {editingSections.visa ? (
                            <div className="add-input-wrapper">
                              <input
                                type="date"
                                id="visaIssue"
                                name="visaIssue"
                                className={`add-wrap-input ${
                                  !values?.visaIssue ||
                                  values.visaIssue === "dd-mm-yyyy"
                                    ? "empty"
                                    : ""
                                }`}
                                placeholder="DD/MM/YYYY"
                                value={
                                  values?.visaIssue
                                    ? formatDateToYYYYMMDD(values.visaIssue)
                                    : ""
                                }
                                onChange={(e) =>
                                  setFieldValue("visaIssue", e.target.value)
                                }
                              />
                              <img
                                src={dateIcon}
                                alt="date icon"
                                className="add-date-input-in"
                              />
                            </div>
                          ) : (
                            <span>
                              {formatDateToYYYYMMDD(values?.visaIssue) ||
                                t("N/A")}
                            </span>
                          )}
                        </div>
                      </div>
                      <CustomErrorMessage
                        name="visaIssue"
                        errors={errors}
                        touched={touched}
                      />
                    </div>

                    {/* Place of Issue */}
                    <div className="form-group">
                      <div className="add-body-input-section">
                        <div className="add-label-container">
                          <label className="add-label" htmlFor="visaIssuePlace">
                            {t("Place of Issue")}
                          </label>
                          {editingSections.visa && (
                            <span className="add-req-mark">*</span>
                          )}
                        </div>
                        <div className="add-input-wrapper">
                          {editingSections.visa ? (
                            <input
                              id="visaIssuePlace"
                              name="visaIssuePlace"
                              className="add-input"
                              placeholder={t("Enter Place Of Issue")}
                              value={values?.visaIssuePlace}
                              onChange={(e) =>
                                setFieldValue("visaIssuePlace", e.target.value)
                              }
                            />
                          ) : (
                            <span>{values?.visaIssuePlace || t("N/A")}</span>
                          )}
                        </div>
                      </div>
                      <CustomErrorMessage
                        name="visaIssuePlace"
                        errors={errors}
                        touched={touched}
                      />
                    </div>

                    {/* Visa Type */}
                    <div className="form-group">
                      <div className="add-body-input-section">
                        <div className="add-label-container">
                          <label className="add-label" htmlFor="visaType">
                            {t("Visa Type")}
                          </label>
                          {editingSections.visa && (
                            <span className="add-req-mark">*</span>
                          )}
                        </div>
                        <div className="add-input-wrapper">
                          {editingSections.visa ? (
                            <AsyncSelect
                              id="visaType"
                              name="visaType"
                              value={values.visaType}
                              onChange={(selectedOption) =>
                                setFieldValue("visaType", selectedOption)
                              }
                              loadOptions={loadVisaTypeOptions}
                              defaultOptions={visaType}
                              styles={asyncSelectStyles}
                              cacheOptions
                              placeholder={t("Select Visa Type")}
                            />
                          ) : (
                            <span>{values?.visaType?.label || t("N/A")}</span>
                          )}
                        </div>
                      </div>
                      <CustomErrorMessage
                        name="visaType"
                        errors={errors}
                        touched={touched}
                      />
                    </div>
                  </div>
                </div>

                {/* Submit button for Visa section (only visible in edit mode) */}
                {editingSections.visa && (
                  <div className="add-footer">
                    <span></span>
                    <div className="add-footer-right">
                      <button
                        type="button"
                        className="add-footer-cancel-btn"
                        onClick={() => {
                          resetForm({ values: initialValues });
                          handleEditToggle("visa");
                        }}
                      >
                        {t("Cancel")}
                      </button>
                      <button
                        type="submit"
                        className="add-footer-submit-btn"
                        disabled={loading}
                      >
                        {loading ? <ButtonLoading /> : t("Submit")}
                      </button>
                    </div>
                  </div>
                )}

                {/* Documents Section Header */}
                <div className="tab-header-container">
                  <h3 className="tab-heading">{t("Documents")}</h3>
                  {currentEmployeeEditing ? (
                    <EditingRestrictionNotice message="View-only" />
                  ) : (
                    !editingSections.documents && (
                      <div
                        className="tab-header-edit-container"
                        onClick={() => handleEditToggle("documents")}
                      >
                        <img src={Edit} alt="edit icon" />
                        <span className="edit-txt">{t("Edit")}</span>
                      </div>
                    )
                  )}
                </div>

                {/* Documents Section Body */}
                {editingSections.documents ? (
                  // Edit Mode
                  <div className="upload-body-tab">
                    {visaError && (
                      <div className="details-error">{visaError}</div>
                    )}
                    {/* Front Document */}
                    <div className="upload-file-input-container m-0">
                      <div className="upload-file-input-container2">
                        <div className="upload-label-container">
                          <label
                            htmlFor="visaDocFront"
                            className="upload-label"
                          >
                            {t("Front Document")}
                          </label>
                          {editingSections.visa && (
                            <span className="upload-req-mark">*</span>
                          )}
                        </div>
                        <div className="remove-btn-wrapper">
                          <div
                            className="upload-file-input-wrapper"
                            onClick={handleOpenFileInput}
                          >
                            <span className="upload-file-input-replacer">
                              {fileNames.frontDocument?.fileName ||
                                fileNames?.frontDocument ||
                                t("No Document")}
                            </span>
                            <input
                              name="frontDocument"
                              ref={fileInputRef1}
                              id="visaDocFront"
                              type="file"
                              className="upload-file-hidden-input"
                              onChange={(e) =>
                                handleDocumentChange(e, setFieldValue)
                              }
                            />
                            <button type="button" className="upload-btn">
                              {t("Upload File")}
                            </button>
                          </div>
                          {fileNames?.frontDocument && (
                            <button
                              type="button"
                              className="remove-file-btn-national"
                              onClick={() =>
                                handleRemoveDocument(
                                  "frontDocument",
                                  setFieldValue
                                )
                              }
                            >
                              {t("Remove")}
                            </button>
                          )}
                        </div>
                      </div>
                      <CustomErrorMessage
                        name="visaDocFront"
                        errors={errors}
                        touched={touched}
                      />
                    </div>

                    {/* Back Document */}
                    <div className="upload-file-input-container m-0">
                      <div className="upload-file-input-container2">
                        <div className="upload-label-container">
                          <label htmlFor="visaDocBack" className="upload-label">
                            {t("Back Document")}
                          </label>
                          {editingSections.visa && (
                            <span className="upload-req-mark">*</span>
                          )}
                        </div>
                        <div className="remove-btn-wrapper">
                          <div
                            className="upload-file-input-wrapper"
                            onClick={handleOpenFileInput2}
                          >
                            <span className="upload-file-input-replacer">
                              {fileNames.backDocument.fileName ||
                                fileNames?.backDocument ||
                                t("No Document")}
                            </span>
                            <input
                              name="backDocument"
                              ref={fileInputRef2}
                              id="visaDocBack"
                              type="file"
                              className="upload-file-hidden-input"
                              onChange={(e) =>
                                handleDocumentChange(e, setFieldValue)
                              }
                            />
                            <button type="button" className="upload-btn">
                              {t("Upload File")}
                            </button>
                          </div>
                          {fileNames?.backDocument && (
                            <button
                              type="button"
                              className="remove-file-btn-national"
                              onClick={() =>
                                handleRemoveDocument(
                                  "backDocument",
                                  setFieldValue
                                )
                              }
                            >
                              {t("Remove")}
                            </button>
                          )}
                        </div>
                      </div>
                      <CustomErrorMessage
                        name="visaDocBack"
                        errors={errors}
                        touched={touched}
                      />
                    </div>
                  </div>
                ) : (
                  // Non-Edit Mode
                  <div className="upload-body-tab">
                    {/* Front Document */}
                    <div className="upload-file-input-container-tab">
                      <div className="upload-file-input-container2">
                        <div className="upload-label-container">
                          <label
                            htmlFor="visaDocFront"
                            className="upload-label"
                          >
                            {t("Front Document")}
                          </label>
                        </div>
                        <div className="document-display-container-tab">
                          <div className="document-info-tab">
                            <PDF />
                            <span className="document-name-tab">
                              {values.visaDocFront
                                ? t("Front Document")
                                : t("No Document Uploaded")}
                            </span>
                          </div>
                          {values.visaDocFront && (
                            <button
                              type="button"
                              className="view-document-btn-tab"
                              onClick={() =>
                                handleViewDocument(
                                  values.visaDocFront,
                                  "Front Document",
                                  "Visa Front"
                                )
                              }
                            >
                              View
                            </button>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Back Document */}
                    <div className="upload-file-input-container-tab">
                      <div className="upload-file-input-container2">
                        <div className="upload-label-container">
                          <label htmlFor="visaDocBack" className="upload-label">
                            Back Document
                          </label>
                        </div>
                        <div className="document-display-container-tab">
                          <div className="document-info-tab">
                            <PDF />
                            <span className="document-name-tab">
                              {values.visaDocBack
                                ? "Back Document"
                                : t("No Document Uploaded")}
                            </span>
                          </div>
                          {values.visaDocBack && (
                            <button
                              type="button"
                              className="view-document-btn-tab"
                              onClick={() =>
                                handleViewDocument(
                                  values.visaDocBack,
                                  "Back Document",
                                  "Visa Back"
                                )
                              }
                            >
                              {t("View")}
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Submit button for Documents section (only visible in edit mode) */}
                {editingSections.documents && (
                  <div className="add-footer">
                    <span></span>
                    <div className="add-footer-right">
                      <button
                        type="button"
                        className="add-footer-cancel-btn"
                        onClick={() => handleEditToggle("documents")}
                      >
                        {t("Cancel")}
                      </button>
                      <button
                        type="submit"
                        className="add-footer-submit-btn"
                        disabled={loading}
                      >
                        {loading ? <ButtonLoading /> : t("Submit")}
                      </button>
                    </div>
                  </div>
                )}
              </Form>
            </>
          )}
        </Formik>
      </div>

      <DocumentViewer
        isOpen={documentViewerOpen}
        onClose={() => setDocumentViewerOpen(false)}
        documentUrl={selectedDocument?.url}
        documentType={selectedDocument?.type}
        documentLabel={selectedDocument?.label}
      />
    </>
  );
};

export default VisaNew;
